/**
 * 灯文舎ダッシュボード — API クライアント
 *
 * GAS Web App (gas-webapp project) の doGet を直接叩く。
 * VITE_API_BASE に GAS deploy URL を入れる前提。
 */

const API_BASE: string = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

export class ApiError extends Error {
  constructor(public status: number, public code: string, message?: string) {
    super(message ?? `${status} ${code}`);
    this.name = 'ApiError';
  }
}

function buildUrl(path: string, params?: Record<string, string | undefined>): string {
  if (!API_BASE) {
    throw new ApiError(0, 'no_api_base', 'VITE_API_BASE が未設定です');
  }
  const url = new URL(API_BASE);
  for (const [k, v] of Object.entries({ endpoint: path.replace(/^\//, ''), ...(params ?? {}) })) {
    if (v != null && v !== '') url.searchParams.set(k, v);
  }
  return url.toString();
}

export async function apiGet<T>(
  endpoint: string,
  params?: Record<string, string | undefined>,
  signal?: AbortSignal
): Promise<T> {
  const res = await fetch(buildUrl(endpoint, params), {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!res.ok) {
    let code = 'http_error';
    try {
      const body = await res.json();
      if (typeof body?.error === 'string') code = body.error;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, code);
  }
  return (await res.json()) as T;
}

/**
 * doPost エンドポイント呼び出し。 出勤管理機能の書込・認証必須エンドポイント用。
 * GAS Web App は CORS preflight (Content-Type: application/json) を受け付けない仕様
 * のため、 text/plain で送り、 サーバ側で JSON.parse する。
 */
export async function apiPost<T>(
  endpoint: string,
  payload: Record<string, unknown>,
  signal?: AbortSignal
): Promise<T> {
  if (!API_BASE) {
    throw new ApiError(0, 'no_api_base', 'VITE_API_BASE が未設定です');
  }
  const body = JSON.stringify({ endpoint, ...payload });
  const res = await fetch(API_BASE, {
    method: 'POST',
    // Apps Script Web App は preflight を返さない。 text/plain なら simple request 扱いで通る。
    headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
    body,
    redirect: 'follow',
    signal,
  });
  if (!res.ok) {
    let code = 'http_error';
    try {
      const errBody = await res.json();
      if (typeof errBody?.error === 'string') code = errBody.error;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, code);
  }
  const data = (await res.json()) as T & { ok?: boolean; error?: string };
  // GAS 側は HTTP 200 で error フラグを返すため、 ok=false は ApiError に昇格
  if (data && (data as { ok?: boolean }).ok === false) {
    throw new ApiError(200, (data as { error?: string }).error ?? 'app_error');
  }
  return data as T;
}
